// ProjectArchivedPage — the archived-repository tombstone (RFC-0010 §9,
// PRD-0001 §1.6.2, AD-0001 §9.3, Task 4.2) at /projects/:repo/archived.
//
// Archived repos lose their doc content; only this tombstone remains, showing
// last-known metadata (name, description, types, owners, tags, archive date) and
// any declared successors (`replaces`) as links to their project pages. No doc
// content is rendered. The tombstone persists indefinitely.

import { Link, useParams } from 'react-router-dom';
import { Archive, ArrowRight, FileText } from '@phosphor-icons/react';
import { PageHeader } from '@/components/ui/page-header';
import { EmptyState } from '@/components/ui/empty-state';
import { ErrorPanel } from '@/components/ui/error-panel';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/query-skeletons';
import { Badge } from '@/components/ui/badge';
import { useProject } from '@/lib/queries/projects';
import { ApiError } from '@/lib/api-client';

function isNotFound(error: unknown): boolean {
  return error instanceof ApiError && error.status === 404;
}

function formatDate(iso: string | null): string | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime();
  if (Number.isNaN(ms)) return null;
  return new Date(ms).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function MetaRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1 border-b border-border py-3 last:border-b-0 sm:flex-row sm:items-baseline sm:gap-4">
      <dt className="w-32 shrink-0 text-[11px] font-semibold uppercase tracking-wide text-muted">
        {label}
      </dt>
      <dd className="text-sm text-text/80">{children}</dd>
    </div>
  );
}

export function ProjectArchivedPage() {
  const { repo } = useParams();
  const { data: project, isLoading, isError, error, refetch } = useProject(repo);

  if (isLoading) {
    return (
      <div role="status" aria-label="Loading project" className="flex flex-col gap-4">
        <Skeleton className="h-8 w-1/3" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (isError || !project || !repo) {
    if (isNotFound(error)) {
      return (
        <div className="rounded-xl border border-border bg-surface shadow-[var(--c-shadow-sm)]">
          <EmptyState
            icon={<FileText size={22} />}
            title="Project not found"
            description="This repository isn't connected to the portal."
            action={
              <Link to="/repositories">
                <Button size="md">Back to repositories</Button>
              </Link>
            }
          />
        </div>
      );
    }
    return <ErrorPanel error={error} onRetry={() => void refetch()} />;
  }

  const archivedAt = formatDate(project.archivedAt);

  return (
    <>
      <PageHeader
        title="Archived repository"
        description="This repository has been archived. Its documentation is no longer served — only this tombstone with last-known metadata and declared successors remains."
      />

      <section className="rounded-xl border border-dashed border-border bg-[color-mix(in_srgb,var(--c-canvas)_60%,transparent)] p-6">
        <div className="mb-5 flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-surface text-muted">
            <Archive size={20} weight="duotone" />
          </span>
          <div>
            <h2 className="text-[18px] font-bold text-text/60 line-through decoration-2">
              {project.name}
            </h2>
            {archivedAt && (
              <p className="text-[12px] text-muted">Archived {archivedAt}</p>
            )}
          </div>
        </div>

        <dl className="mb-2">
          {project.description && <MetaRow label="Description">{project.description}</MetaRow>}
          {project.types.length > 0 && (
            <MetaRow label="Types">
              <span className="flex flex-wrap gap-1.5">
                {project.types.map((t) => (
                  <Badge key={t} variant="outline">
                    {t}
                  </Badge>
                ))}
              </span>
            </MetaRow>
          )}
          {project.owners.length > 0 && (
            <MetaRow label="Owners">{project.owners.join(', ')}</MetaRow>
          )}
          {project.tags.length > 0 && (
            <MetaRow label="Tags">
              <span className="flex flex-wrap gap-1.5">
                {project.tags.map((t) => (
                  <Badge key={t} variant="ghost">
                    {t}
                  </Badge>
                ))}
              </span>
            </MetaRow>
          )}
        </dl>

        {/* Successors — declared via other repos' `replaces:` (AD-0001 §9.3). */}
        <div className="mt-6 border-t border-border pt-5">
          <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-muted">
            Successors
          </h3>
          {project.replaces.length > 0 ? (
            <ul className="flex flex-col gap-2">
              {project.replaces.map((r) => (
                <li key={r.targetRepoName}>
                  <Link
                    to={`/projects/${encodeURIComponent(r.targetRepoName)}`}
                    className="group inline-flex items-center gap-2 text-sm font-medium text-text/80 transition-colors hover:text-text"
                  >
                    {r.targetRepoName}
                    <ArrowRight
                      size={14}
                      weight="bold"
                      className="text-text/30 transition-colors group-hover:text-accent"
                    />
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-muted">No successor repositories were declared.</p>
          )}
        </div>
      </section>
    </>
  );
}
